import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { h, ref, Ref, computed } from 'vue';
import { autocompleteField, dateTimeField, inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { validate } from '@/entities/issue/v2';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { LegacyPhoto } from '@/ui/pages/issue/legacyTypes';
import AddPhotos from '@/_legacy/components/Issues/AddPhotos.vue';
import StatusSelector from '@/_legacy/components/Issues/StatusSelector.vue';
import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
import { Field } from '@/ui/components/form/Field';
import { hasPermission, Permissions, user } from '@/ui/auth/auth';
import { IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { IssueStatus } from '@/entities/todo';
import * as issueAssetsPhotos from '@/services/assets-api/issueAssetsPhotos';
import isEmail from 'validator/lib/isEmail';
import { useTimeZonedDates } from '@/ui/concerns/useTimeZonedDates';
import AssigneesField from '@/ui/pages/user/assignees/AssigneesField.vue';

export function useIssueForm(record: Ref<IssueRecordEditable>, task: Task) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation<IssueRecordEditable>(validate, record, task.state);

  const { getDateInUserTimeZone, getDateInLocalTimeZone } = useTimeZonedDates();

  const timeZonedDeadline = computed(
    () => record.value.deadline === null ? null : getDateInUserTimeZone(record.value.deadline),
  );

  const issueAssetsLoading = ref(false);
  const submitIsLoading = computed(() => task.state.value.pending || issueAssetsLoading.value);

  const handleSubmit = async () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      issueAssetsLoading.value = true;
      record.value = await issueAssetsPhotos.processPhotos(record.value);
      issueAssetsLoading.value = false;

      task.run();
    }
  };

  const extractUserValue = (value: string | number) => value.toString().replace('user_', '');

  const formFields = <T = number>(
    entityItemOptions: SelectOptions,
    userOptions: SelectOptions<T>,
    disabledExternalAssignee: boolean,
    showEntityItemSelection: boolean,
    fetchUsersTask: Task,
    nonVisibleOptions?: SelectOptions<T>,
  ) => [
    inputField(
      {
        name: 'title',
        label: t('common.title'),
        placeholder: t('issues.form.enter-title'),
        value: record.value.title,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        maxlength: 255,
      },

      value => {
        record.value.title = value;
      },
    ),

    inputField(
      {
        type: 'textarea',
        name: 'description',
        label: t('common.description'),
        placeholder: t('issues.form.enter-description'),
        value: record.value.description ?? '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.description = value;
      },
    ),
    h(Field, {
      props: {
        name: 'photos',
        label: '',
        required: false,
        errors: validationErrors.value,
        hideLabel: true,
      },
    }, [h(AddPhotos, {
      props: {
        value: record.value.photos ?? [],
      },
      on: {
        input: (value: Array<LegacyPhoto>) => {
          record.value.photos = value;
        },
      },
    })]),

    h(Field, {
      props: {
        name: 'status',
        label: t('common.status'),
        required: false,
        errors: validationErrors.value,
      },
    }, [h(StatusSelector, {
      props: {
        value: record.value.status,
      },
      on: {
        input: (value: number) => {
          record.value.status = value;

          //can't use disabledExternalAssignee here as it updates after, not before
          if ([IssueStatus.Done, IssueStatus.CantDo].includes(record.value.status)) {
            record.value.associateAssignees = [];
          }
        },
      },
    })]),

    entityItemOptions.length > 0 && showEntityItemSelection
      ? autocompleteField({
        name: 'entity_item_id',
        label: t('common.entity-item'),
        value: record.value.entityItem,
        placeholder: t('sensors.form.enter-entity-item'),
        required: false,
        data: entityItemOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        limit: ENTITY_ITEMS_DROPDOWN_LIMIT,
      },

      value => {
        record.value.entityItem = value;
      })
      : null,

    ...(hasPermission(Permissions.CREATE_ISSUE_WITH_ASSIGNEE)
      ? [h(AssigneesField, {
        props: {
          value: [
            ...record.value.assignees,
            ...record.value.associateAssignees,
            ...record.value.assignedGroups,
          ],
          options: userOptions,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
          disabledExternalAssignee,
          nonVisibleOptions,
        },
        on: {
          'update-options': fetchUsersTask.run,
          input: (value: Array<string|number>) => {
            const assignees: Array<string> = [];
            const associateAssignees: Array<string> = [];
            const groups: Array<string> = [];

            value.forEach((v: string|number) => {
              const _value = extractUserValue(v);

              if (isEmail(_value)) {
                associateAssignees.push(_value);
              } else if ((v as string).includes('user_')) {
                assignees.push(v as string);
              } else if ((v as string).includes('group_')) {
                groups.push(v as string);
              }
            });

            record.value.assignees = assignees;
            record.value.associateAssignees = associateAssignees;
            record.value.assignedGroups = groups;
          },
        },
      })]
      : []),

    h(Field, {
      props: {
        name: 'priority',
        label: t('common.priority'),
        required: false,
        errors: validationErrors.value,
      },
    }, [h(PrioritySelector, {
      props: {
        value: record.value.priority,
      },
      on: {
        input: (value: number) => {
          record.value.priority = value;
        },
      },
    })]),

    dateTimeField(
      {
        name: 'deadline',
        label: t('common.deadline'),
        value: timeZonedDeadline.value,
        placeholder: t('issues.form.enter-deadline'),
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        clearable: true,
        position: 'is-top-right',
        locale: user.value.locale,
        datePickerClass: 'datepicker--extended-cell',
      },
      value => {
        if (value === null) {
          record.value.deadline = null;

          return;
        }

        record.value.deadline = getDateInLocalTimeZone(value);
      },
    ),

    apiErrorMessage(task.state.value),
  ];

  return {
    formFields,
    handleSubmit,
    submitIsLoading,
  };
}
